import { useState, useRef } from 'react';
import { CTABanner } from '../../components/ui/UtilsLayout';
import { imagePath } from '../../utils/imagePath';
import {
  ImageWithFeatureList,
  IndustriesImagesCard,
  RetailECommerceFeatureCard,
} from '../../utils/IndustriesData/IndustriesData';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CalendlyScheduler } from '../../components/ScheduleDemoComponents/ScheduleDemoComponents';
import { RetailEcommerceFeaturesData } from '../../components/data/data/Industries/IndustriesStaticData';

export const RetailEcommerce = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showScheduler, setShowScheduler] = useState(false);

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const openScheduler = () => setShowScheduler(true);
  const closeScheduler = () => setShowScheduler(false);

  const cards = [
    {
      image: imagePath.Americana,
      title: 'Americana',
      description:
        'An AR-based product experience enabling shoppers to explore food products through interactive recipes tailored for seasonal and festival dishes at grocery touchpoints.',
      to: '/all-industries/americana',
    },
    {
      image: imagePath.RST,
      title: 'RST Trading',
      description:
        'A QR-based WebAR experience where scanning assets launched an interactive product showcase with guided narration, animations, and seamless transitions across devices.',
      to: '/all-industries/rst-trading',
    },
    {
      image: imagePath.Cremeweb21,
      title: 'Creme 21',
      description:
        "An AR experience featuring an AI avatar showcasing Creme 21's skin oil range through an interactive infomercial, driving product education & engagement at retail touchpoints.",
      to: '/all-industries/creme21',
    },
  ];

  const TOTAL = cards.length;

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % TOTAL);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + TOTAL) % TOTAL);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      diff > 0 ? nextSlide() : prevSlide();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className="relative pt-14 sm:pt-20 md:pt-0">
      <div className="hidden lg:block absolute -top-[460px] right-[10px] md:right-[20px] lg:right-[0px]">
        <img
          className="w-[200px] md:w-[250px] lg:w-[430px] h-auto object-contain"
          src={imagePath?.reaearch1}
          alt="Grid AI"
        />
      </div>

      {/* Hero / Image Section */}
      <section className="relative z-10 mt-4 sm:mt-6 md:mt-24 lg:mt-10 flex items-start lg:items-center px-3 sm:px-4 md:px-6 lg:px-8 py-8 lg:py-28">
        <div className="max-w-7xl w-full mx-auto">
          <IndustriesImagesCard
            title="Retail and"
            subtitle="E-Commerce"
            description="Turn browsers into buyers with immersive product experiences."
            image={imagePath?.retailECommerce}
            mobileImage={imagePath?.retailEcommerceMobile}
            buttonText="Schedule a Demo"
            loading="eager"
            fetchpriority="high"
            onButtonClick={openScheduler}
          />
        </div>
      </section>

      <div className="hidden lg:block absolute sm:top-[320px] md:top-[320px] lg:top-[655px] left-0 z-[0]">
        <img
          className="w-[100px] sm:w-[180px] md:w-[460px] lg:w-[530px] h-auto object-contain"
          src={imagePath?.Research4}
          alt="Grid AI"
        />
      </div>

      <section className="relative z-10 w-full bg-[#1E1E22] py-8 sm:py-12 md:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10">
          {/* Section Heading */}
          <div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 max-w-full sm:max-w-6xl">
            <h2 className="text-[#FFFFFF] text-2xl sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold mb-2 sm:mb-3 md:mb-4 leading-tight">
              Our Case Studies
            </h2>
            <p className="text-[#FFFFFF] text-md sm:text-sm md:text-base lg:text-lg leading-relaxed">
              Delivering proven results across retail and e-commerce with
              immersive product visualization and interactive shopping
              experiences—helping brands boost engagement, reduce returns, and
              drive higher conversion rates.
            </p>

            {/* Mobile Arrow Navigation */}
            <div className="flex sm:hidden gap-2 mt-4 justify-end">
              <button
                onClick={prevSlide}
                className="w-8 h-8 flex items-center justify-center bg-[#31333A] hover:bg-[#3d3f47] rounded-full transition-colors"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={nextSlide}
                className="w-8 h-8 flex items-center justify-center bg-[#31333A] hover:bg-[#3d3f47] rounded-full transition-colors"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Mobile Carousel */}
          <div
            className="sm:hidden relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {cards.map((card, index) => (
                <div key={index} className="flex-shrink-0 w-full px-1">
                  <RetailECommerceFeatureCard
                    image={card.image}
                    title={card.title}
                    description={card.description}
                    to={card.to}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Desktop/Tablet Grid */}
          <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {cards.map((card, index) => (
              <RetailECommerceFeatureCard
                key={index}
                image={card.image}
                title={card.title}
                description={card.description}
                to={card.to}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Image With Feature List */}
      <section className="w-full py-10 sm:py-14 md:py-16 lg:py-20 px-0 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ImageWithFeatureList
            image={imagePath?.experienceProduct}
            mobileFullImage={imagePath?.experienceProductMobile}
            imageTitle="Experience Products. Anywhere. Instantly."
            features={RetailEcommerceFeaturesData}
          />
        </div>
      </section>

      <div className="hidden md:block absolute bottom-[-80px] md:-left-40 lg:left-0">
        <img
          className="w-[180px] md:w-[260px] lg:w-[460px] h-auto object-contain rotate-180"
          src={imagePath?.research3}
          alt="Grid AI"
        />
      </div>

      <section className="w-full mx-auto px-0 sm:px-6 lg:px-8 py-12 max-w-[1350px]">
        <div className="relative sm:left-0 md:left-4">
          <CTABanner
            welcomeText="Powerful Product Experiences, Data-Driven Results"
            description="Create immersive shopping experiences using spatial models, templates, and AR. Harness analytics to refine offerings, personalize journeys, and improve conversion rates."
            buttonText="Contact Us"
            navigateTo="/contact-us"
          />
        </div>
      </section>

      {showScheduler && <CalendlyScheduler onClose={closeScheduler} />}
    </div>
  );
};
